import {
  DefaultApi,
  GetWebDashboard200Response,
  GetWebDashboardPlanUsage200Response,
  ListWebDashboardCustomThemes200Response,
  UserExtraType,
  WebDashboardSummary,
} from "@coeff/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiContext } from "../utils/contexts";

import { bumpDashboardWriteEpoch, enqueueDashboardWrite } from "./dashboardWriteChain";

import { QueryCacheKey } from ".";

const fetchWebDashboards = async (api: DefaultApi): Promise<WebDashboardSummary[]> => {
  const response = await api.listWebDashboards();
  return response.data.dashboards ?? [];
};

/** Restore/unarchive can fail two ways, both as 409s, disambiguated by the error `type`. */
export type RestoreError =
  | { kind: "plan_limit"; planUsage: { used: number; limit: number } | null }
  | { kind: "restore_locked" }
  | { kind: "other" };

/**
 * Parse an archive/restore error into the case the UI should surface: the family plan limit was
 * reached (with usage numbers for the progress bar), this dashboard's per-dashboard restore cap is
 * exhausted, or an unclassified failure.
 */
export const parseRestoreError = (err: unknown): RestoreError => {
  const response = (
    err as {
      response?: {
        status?: number;
        data?: { type?: string; plan_limit?: number; plan_usage_count?: number };
      };
    }
  )?.response;
  if (response?.status === 409) {
    if (response.data?.type === "web_dashboard_plan_limit_reached") {
      const { plan_limit, plan_usage_count } = response.data;
      return {
        kind: "plan_limit",
        planUsage:
          plan_limit != null && plan_usage_count != null
            ? { used: plan_usage_count, limit: plan_limit }
            : null,
      };
    }
    return { kind: "restore_locked" };
  }
  return { kind: "other" };
};

export const useWebDashboards = (options?: { enabled?: boolean }) => {
  const { apiClient: api } = useApiContext();

  return useQuery([QueryCacheKey.WEB_DASHBOARDS], () => fetchWebDashboards(api), {
    enabled: options?.enabled,
  });
};

const fetchWebDashboardCustomThemes = async (
  api: DefaultApi
): Promise<ListWebDashboardCustomThemes200Response> => {
  const response = await api.listWebDashboardCustomThemes();
  return response.data;
};

/** The current user's custom dashboard themes (newest first), for the theme selector. */
export const useWebDashboardCustomThemes = (options?: { enabled?: boolean }) => {
  const { apiClient: api } = useApiContext();

  return useQuery(
    [QueryCacheKey.WEB_DASHBOARD_CUSTOM_THEMES],
    () => fetchWebDashboardCustomThemes(api),
    {
      enabled: options?.enabled,
      // Themes are created outside this component (chat/agent, CLI), so every selector open
      // must fetch fresh — the app-wide 5-minute staleTime would show a stale list.
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
};

/** Remember the last light/dark value the user explicitly selected for future dashboards. */
export const useSaveWebDashboardThemeModePreference = () => {
  const { apiClient: api } = useApiContext();

  return useMutation((mode: "light" | "dark") =>
    api.setUserExtra({
      set_user_extra_request: {
        user_extra_type: UserExtraType.WebDashboardThemePreference,
        user_extra: { web_dashboard_theme_mode: mode },
      },
    })
  );
};

/** Soft-delete one of the user's custom themes (dashboards on it fall back to the light theme). */
export const useDeleteWebDashboardCustomTheme = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    (themeId: string) => api.deleteWebDashboardCustomTheme({ theme_id: themeId }),
    {
      onSuccess: () => queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_CUSTOM_THEMES]),
    }
  );
};

/** Set or remove a custom theme as the domain-wide default for new dashboards (admins only). */
export const useSetWebDashboardCustomThemeDefault = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    (params: { themeId: string; isDefault: boolean }) =>
      api.setWebDashboardCustomThemeDefault({
        theme_id: params.themeId,
        set_web_dashboard_custom_theme_default_request: { is_default: params.isDefault },
      }),
    {
      onSuccess: () => queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_CUSTOM_THEMES]),
    }
  );
};

/** Upload one variant ("light"/"dark" background) of the domain white-label logo (admins only). */
export const useSetWebDashboardCustomLogo = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    (params: { variant: "light" | "dark"; imageBase64: string }) =>
      api.setWebDashboardCustomLogo({
        set_web_dashboard_custom_logo_request: {
          variant: params.variant,
          image_base64: params.imageBase64,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_CUSTOM_THEMES]);
        // The nav logo renders from the dashboard response's custom_branding.
        queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD]);
      },
    }
  );
};

/** Remove one variant of the domain white-label logo (admins only). */
export const useDeleteWebDashboardCustomLogo = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation((variant: "light" | "dark") => api.deleteWebDashboardCustomLogo({ variant }), {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_CUSTOM_THEMES]);
      // The nav logo renders from the dashboard response's custom_branding.
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD]);
    },
  });
};

/** Restore a soft-deleted custom theme — the delete toast's Undo action. */
export const useRestoreWebDashboardCustomTheme = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    (themeId: string) => api.restoreWebDashboardCustomTheme({ theme_id: themeId }),
    {
      onSuccess: () => queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_CUSTOM_THEMES]),
    }
  );
};

const fetchWebDashboardPlanUsage = async (
  api: DefaultApi
): Promise<GetWebDashboardPlanUsage200Response> => {
  const response = await api.getWebDashboardPlanUsage();
  return response.data;
};

export const useWebDashboardPlanUsage = (options?: { enabled?: boolean }) => {
  const { apiClient: api } = useApiContext();

  return useQuery([QueryCacheKey.WEB_DASHBOARD_PLAN_USAGE], () => fetchWebDashboardPlanUsage(api), {
    enabled: options?.enabled,
  });
};

export const useRenameWebDashboardMutation = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  // Chained with the viewer's saves: the rename PATCH bumps modified_dt, so landing
  // mid-flight would 409 an in-flight save built on the pre-rename token.
  const mutator = (params: { web_dashboard_id: string; title: string }) =>
    enqueueDashboardWrite(params.web_dashboard_id, async () => {
      // OCC-guard the PATCH with the cached token (when the viewer's single-dashboard
      // cache exists — the list page has none, keeping legacy no-check behavior there).
      // Without it a 200 here would adopt a fresh token into a cache whose
      // content_config may predate another writer, letting the next save token-match
      // and silently full-write over that content.
      const cachedDashboard = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        params.web_dashboard_id,
      ]);

      const response = await api.patchWebDashboard({
        web_dashboard_id: params.web_dashboard_id,
        patch_web_dashboard_request: {
          content_config: { title: params.title },
          expected_modified_dt: cachedDashboard?.modified_dt,
        },
      });

      // Optimistically patch the single-dashboard cache the viewer reads from, so the
      // navbar title and context dashboardName update with no reload/refetch. Partial
      // (exact: false) match covers both [WEB_DASHBOARD, id] and
      // [WEB_DASHBOARD, id, screenshotToken] key variants. Carry the fresh concurrency
      // token too — the rename bumped the row's modified_dt, so keeping the old token
      // would make the user's own next save 409. Done inside the chain link (not
      // onSuccess) so the next chained save reads the post-rename token.
      //
      // A save already queued behind this rename captured its payload (old title) before
      // the PATCH landed; bump the epoch so it pairs that payload with its enqueue-time
      // token and rebases via its delta, instead of token-matching and full-writing the
      // old title back.
      bumpDashboardWriteEpoch(params.web_dashboard_id);
      queryClient.setQueriesData(
        { queryKey: [QueryCacheKey.WEB_DASHBOARD, params.web_dashboard_id], exact: false },
        (old: any) =>
          old
            ? {
                ...old,
                modified_dt: response.data.modified_dt ?? old.modified_dt,
                content_config: { ...old.content_config, title: params.title },
              }
            : old
      );
      return response.data;
    });

  return useMutation(mutator, {
    onSuccess: () => {
      // Refresh the "My Dashboards" list.
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARDS]);
    },
    onError: (_err, params) => {
      // Likely a 409 from the OCC guard: the dashboard changed since this tab last read
      // it. Refetch so the cached token/config resnap to the row and a retry can succeed.
      queryClient.invalidateQueries({
        queryKey: [QueryCacheKey.WEB_DASHBOARD, params.web_dashboard_id],
        exact: false,
      });
    },
  });
};

export const useArchiveWebDashboardMutation = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  const mutator = async (params: {
    web_dashboard_id: string;
    archived: boolean;
    // Set when unarchiving is an immediate undo of a just-performed archive, so the backend can
    // reverse it for free (no restore consumed) within the undo window.
    is_undo?: boolean;
  }) => {
    const response = await api.archiveWebDashboard({
      web_dashboard_id: params.web_dashboard_id,
      archive_web_dashboard_request: { archived: params.archived, is_undo: params.is_undo },
    });
    return response.data;
  };

  return useMutation(mutator, {
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARDS]);
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD, params.web_dashboard_id]);
      queryClient.invalidateQueries([
        QueryCacheKey.WEB_DASHBOARD_PERMISSIONS,
        params.web_dashboard_id,
      ]);
      // Keep the admin list (and its active count) in sync after archive/restore.
      queryClient.invalidateQueries([QueryCacheKey.ADMIN_EXT_WEB_DASHBOARDS]);
      // Usage count / grace state may have changed (e.g. archiving down clears the grace banner).
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_PLAN_USAGE]);
    },
  });
};

export const useDuplicateWebDashboardMutation = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  const mutator = async (params: { web_dashboard_id: string; title?: string }) => {
    const response = await api.duplicateWebDashboard({
      web_dashboard_id: params.web_dashboard_id,
      duplicate_web_dashboard_request: { title: params.title },
    });
    return response.data;
  };

  return useMutation(mutator, {
    onSuccess: () => {
      // Surface the new copy in the "My Dashboards" list and reflect the higher usage count.
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARDS]);
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_PLAN_USAGE]);
    },
  });
};

export const useDeleteWebDashboardMutation = () => {
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();

  const mutator = async (web_dashboard_id: string) => {
    const response = await api.deleteWebDashboard({ web_dashboard_id });
    return response.data;
  };

  return useMutation(mutator, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARDS]);
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_PLAN_USAGE]);
    },
  });
};
